import type { IndividualEvent, Comment } from "@/types/generated";

// Extended type to include optional comments array for card display
type IndividualEventWithComments = IndividualEvent & {
  comments?: Comment[];
};
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "./ui/card";
import { formatDistanceToNow } from "date-fns";
import { formatLapTime } from "./ui/lap-time";
import LikeButton from "./LikeButton";
import { Button } from "./ui/button";
import { MessageCircle } from "lucide-react";
import { useState } from "react";
import { CommentInput } from "./CommentInput";
import { CommentsList } from "./CommentsList";
import { Link } from "@inertiajs/react";

interface TrackEventCardProps {
  trackEvent: IndividualEventWithComments;
}

export function TrackEventCard({ trackEvent }: TrackEventCardProps) {
  const [showCommentInput, setShowCommentInput] = useState(false);

  const fullPageUrl = `/event/${trackEvent.id}/laps`;
  const userName = `${trackEvent.user.first_name} ${trackEvent.user.last_name}`;
  const timestamp = trackEvent.session_started_at
    ? formatDistanceToNow(new Date(trackEvent.session_started_at), {
        addSuffix: true,
      })
    : null;

  return (
    <Link
      href={fullPageUrl}
      className="block my-4"
      onClick={(e) => e.stopPropagation()}
    >
      <Card className="transition-all duration-200 hover:shadow-lg hover:border-orange-300/40">
        <CardHeader className="gap-3">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1 flex-1">
              <div className="text-sm leading-normal">
                <span className="font-semibold">{userName}</span>
                {timestamp && (
                  <span className="text-muted-foreground"> · {timestamp}</span>
                )}
              </div>
              <div className="text-sm text-muted-foreground">
                <span className="font-medium">{trackEvent.track?.name}</span>
                {trackEvent.sim_car && (
                  <>
                    {" · "}
                    <span>
                      {trackEvent.sim_car.manufacturer}{" "}
                      {trackEvent.sim_car.model}
                    </span>
                  </>
                )}
              </div>
              {trackEvent.title && (
                <CardTitle className="leading-tight">
                  {trackEvent.title}
                </CardTitle>
              )}
            </div>
            <div className="text-xs font-ocr uppercase text-muted-foreground">
              {trackEvent.session_type}
            </div>
          </div>
          {trackEvent.description && (
            <CardDescription className="leading-normal">
              {trackEvent.description}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <div>
              <div className="text-sm text-muted-foreground">Laps</div>
              <div className="text-lg font-semibold font-ocr">
                {trackEvent.lap_count ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Best Lap</div>
              <div className="text-lg font-semibold font-ocr">
                {trackEvent.best_lap_time_ms
                  ? formatLapTime(trackEvent.best_lap_time_ms, true)
                  : "—"}
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Position</div>
              <div className="text-lg font-semibold font-ocr">
                {trackEvent.finish_position && trackEvent.finish_position > 0 ? trackEvent.finish_position : "—"}/{trackEvent.total_drivers}
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2 sm:ml-auto w-full sm:w-auto justify-end">
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCommentInput(!showCommentInput);
                }}
                variant="ghost"
                size="sm"
                className="flex items-center gap-1.5 transition-all duration-300 hover:bg-transparent hover:text-current"
              >
                <MessageCircle className="h-5 w-5" />
                {trackEvent.comments && trackEvent.comments.length > 0 && (
                  <span className="text-sm font-medium">
                    {trackEvent.comments.length}
                  </span>
                )}
              </Button>
              <div onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}>
                <LikeButton
                  trackEventId={trackEvent.id}
                  isLiked={trackEvent.liked_by_current_user}
                  likesCount={trackEvent.likes_count}
                />
              </div>
            </div>
          </div>

          {trackEvent.comments && trackEvent.comments.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <CommentsList
                comments={trackEvent.comments}
                maxComments={3}
                eventUrl={fullPageUrl}
              />
            </div>
          )}

          {showCommentInput && (
            <div className="mt-3 pt-3 border-t border-t-neutral-200">
              <CommentInput
                commentableType="TrackEvent"
                commentableId={trackEvent.id}
                onCancel={() => setShowCommentInput(false)}
                autoFocus
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}
